import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOCK_MEMBERS = [
  { user_id: '1', name: 'Erik Johansen', role: 'Lead Attorney', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face', status: 'online' },
  { user_id: '2', name: 'Maria Hansen', role: 'Associate', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face', status: 'online' },
  { user_id: '3', name: 'Anders Berg', role: 'Paralegal', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face', status: 'offline' },
  { user_id: '4', name: 'Sofie Olsen', role: 'Junior Associate', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face', status: 'offline' },
];

const MOCK_MESSAGES = [
  { id: '1', sender_name: 'Erik Johansen', sender_id: '1', content: 'I\'ve reviewed the shareholder agreement. There are some key clauses we need to discuss with the client regarding minority protection.', created_at: new Date(Date.now() - 3600000 * 2).toISOString() },
  { id: '2', sender_name: 'Maria Hansen', sender_id: '2', content: 'Agreed. I noticed the drag-along clause might be problematic. Should we schedule a call with the client tomorrow?', created_at: new Date(Date.now() - 3600000 * 1.5).toISOString() },
  { id: '3', sender_name: 'Anders Berg', sender_id: '3', content: 'I\'ve prepared a summary of all the relevant case law. Uploading it to the documents folder now.', created_at: new Date(Date.now() - 3600000).toISOString() },
  { id: '4', sender_name: 'Erik Johansen', sender_id: '1', content: 'Perfect. Let\'s aim for a team sync at 2pm to go through everything before the client meeting.', created_at: new Date(Date.now() - 1800000).toISOString() },
];

const TeamChat = ({ caseId }) => {
  const { i18n } = useTranslation();
  const en = i18n.language === 'en' || i18n.language?.startsWith('en');
  
  const [messages, setMessages] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (caseId) {
      fetchData();
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [caseId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchData = async () => {
    try {
      const [messagesRes, membersRes] = await Promise.all([
        axios.get(`${API}/cases/${caseId}/chat/internal`).catch(() => null),
        axios.get(`${API}/cases/${caseId}/chat/members`).catch(() => null)
      ]);
      const fetchedMessages = messagesRes?.data?.messages || [];
      const fetchedMembers = membersRes?.data?.members || [];
      if (fetchedMessages.length === 0 && fetchedMembers.length === 0) {
        setUseMockData(true);
        setMessages(MOCK_MESSAGES);
        setMembers(MOCK_MEMBERS);
      } else {
        setMessages(fetchedMessages);
        setMembers(fetchedMembers);
      }
    } catch {
      setUseMockData(true);
      setMessages(MOCK_MESSAGES);
      setMembers(MOCK_MEMBERS);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (useMockData) return;
    try {
      const response = await axios.get(`${API}/cases/${caseId}/chat/internal`);
      setMessages(response.data.messages || []);
    } catch {}
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    
    if (useMockData) {
      setMessages(prev => [...prev, { id: String(Date.now()), sender_name: 'Erik Johansen', sender_id: '1', content: newMessage, created_at: new Date().toISOString() }]);
      setNewMessage('');
      setSending(false);
      return;
    }
    
    try {
      await axios.post(`${API}/cases/${caseId}/chat/internal`, { content: newMessage });
      setNewMessage('');
      await fetchMessages();
    } catch {
      toast.error(en ? 'Could not send message' : 'Kunne ikke sende melding');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleTimeString(en ? 'en-US' : 'nb-NO', { hour: '2-digit', minute: '2-digit' });
  };

  const getMember = (id: string, name: string) => members.find(m => m.user_id === id || m.name === name) || { name, avatar: null };

  const getInitials = (name: string) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
      </div>
    );
  }

  const onlineMembers = members.filter(m => m.status === 'online');
  const offlineMembers = members.filter(m => m.status !== 'online');

  return (
    <div className="h-full flex">
      {/* Main Chat */}
      <div className="flex-1 flex flex-col">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-8 py-10">
            {/* Welcome Header */}
            <div className="mb-12">
              <h1 className="text-xl font-semibold text-gray-900 mb-1">Team Chat</h1>
              <p className="text-sm text-gray-400">
                {en ? 'Internal team discussion' : 'Intern teamdiskusjon'}
              </p>
            </div>

            {/* Messages */}
            <div className="space-y-8">
              {messages.map((msg, idx) => {
                const member = getMember(msg.sender_id, msg.sender_name);
                const prev = messages[idx - 1];
                const showHeader = !prev || prev.sender_id !== msg.sender_id || 
                  new Date(msg.created_at).getTime() - new Date(prev.created_at).getTime() > 300000;
                
                return (
                  <div key={msg.id} className={showHeader ? '' : '-mt-5'}>
                    {showHeader && (
                      <div className="flex items-start gap-3 mb-1">
                        {member.avatar ? (
                          <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-500 flex-shrink-0">
                            {getInitials(member.name)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-900">{msg.sender_name}</span>
                            <span className="text-xs text-gray-400">{formatTime(msg.created_at)}</span>
                          </div>
                          <p className="text-[15px] text-gray-600 leading-relaxed">{msg.content}</p>
                        </div>
                      </div>
                    )}
                    {!showHeader && (
                      <div className="pl-[52px]">
                        <p className="text-[15px] text-gray-600 leading-relaxed">{msg.content}</p>
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>

        {/* Input */}
        <div className="border-t border-gray-100 p-4">
          <div className="max-w-2xl mx-auto">
            <form onSubmit={sendMessage}>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={en ? 'Write a message...' : 'Skriv en melding...'}
                  disabled={sending}
                  className="flex-1 h-11 px-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-300 focus:ring-0 transition-colors"
                />
                <button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="h-11 px-5 flex items-center justify-center gap-2 rounded-lg bg-gray-900 text-white text-sm font-medium disabled:bg-gray-200 disabled:text-gray-400 transition-colors hover:bg-gray-800"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span>{en ? 'Send' : 'Send'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Members Sidebar */}
      <div className="w-64 border-l border-gray-100 flex-shrink-0 hidden lg:flex flex-col">
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">{en ? 'Members' : 'Medlemmer'}</h2>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Online */}
          {onlineMembers.length > 0 && (
            <div className="mb-6">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3 px-1">
                Online · {onlineMembers.length}
              </p>
              <div className="space-y-1">
                {onlineMembers.map((m) => (
                  <div key={m.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="relative flex-shrink-0">
                      <img src={m.avatar} alt={m.name} className="w-9 h-9 rounded-full object-cover" />
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
                      <p className="text-xs text-gray-400 truncate">{m.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Offline */}
          {offlineMembers.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3 px-1">
                Offline · {offlineMembers.length}
              </p>
              <div className="space-y-1">
                {offlineMembers.map((m) => (
                  <div key={m.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <img src={m.avatar} alt={m.name} className="w-9 h-9 rounded-full object-cover opacity-50 grayscale flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500 truncate">{m.name}</p>
                      <p className="text-xs text-gray-400 truncate">{m.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamChat;
